import axios from '../config/axiosConfig';

const API_URL = '/export/';

// Export Executive Summary PDF
const exportExecutiveSummary = async (projectId = null) => {
  const url = projectId 
    ? `${API_URL}executive-summary/${projectId}`
    : `${API_URL}executive-summary`;
    
  try {
    const response = await axios.get(url, {
      responseType: 'blob'
    });
    
    // Create download link
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `executive-summary-${Date.now()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    return true;
  } catch (error) {
    console.error('Error exporting executive summary:', error);
    throw error;
  }
};

// Export Excel Data
const exportExcel = async (projectId = null) => {
  const url = projectId 
    ? `${API_URL}excel/${projectId}`
    : `${API_URL}excel`;
    
  try {
    const response = await axios.get(url, {
      responseType: 'blob'
    });
    
    // Create download link
    const blob = new Blob([response.data], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `red-team-data-${Date.now()}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    return true;
  } catch (error) {
    console.error('Error exporting Excel data:', error);
    throw error;
  }
};

// Generate MITRE Navigator Layer client-side (Updated to v17 format)
const generateMitreNavigatorLayer = (actions, projectName = 'Red Team Operations') => {
  // MITRE Navigator layer format v17
  const layer = {
    name: projectName,
    versions: {
      attack: "15",  // Updated to latest major version
      navigator: "5.0.1",  // Updated to v17 equivalent
      layer: "4.5"
    },
    domain: "enterprise-attack",
    description: `MITRE ATT&CK coverage for ${projectName} - Generated by Red Team Logger`,
    filters: {
      platforms: [
        "Windows",
        "macOS", 
        "Linux",
        "PRE",
        "Network",
        "Containers",
        "Office 365",
        "Azure AD",
        "Google Workspace", 
        "SaaS",
        "IaaS",
        "Azure",
        "AWS",
        "GCP"
      ]
    },
    sorting: 0,
    layout: {
      layout: "side",
      aggregateFunction: "average",
      showID: true,  // Show technique IDs
      showName: true,
      showAggregateScores: false,
      countUnscored: false,
      expandedSubtechniques: "none"  // New in v17
    },
    hideDisabled: false,
    techniques: [],
    gradient: {
      colors: [
        "#ff6666",  // Red - blocked/failed
        "#ffe766",  // Yellow - detected  
        "#8ec843"   // Green - successful
      ],
      minValue: 0,
      maxValue: 100
    },
    legendItems: [
      {
        label: "Successful Techniques",
        color: "#8ec843",
        description: "Techniques that were executed successfully"
      },
      {
        label: "Detected Techniques", 
        color: "#ffe766",
        description: "Techniques that were detected by security controls"
      },
      {
        label: "Blocked/Failed Techniques",
        color: "#ff6666", 
        description: "Techniques that were blocked or failed to execute"
      }
    ],
    metadata: [
      {
        name: "Generated By",
        value: "Red Team Logger"
      },
      {
        name: "Generation Date",
        value: new Date().toISOString().split('T')[0]
      },
      {
        name: "Project",
        value: projectName
      }
    ],
    links: [],
    showTacticRowBackground: false,
    tacticRowBackground: "#dddddd",
    selectTechniquesAcrossTactics: true,
    selectSubtechniquesWithParent: false,
    viewMode: 0,  // New in v17
    hideDisabled: false,
    customDataURL: "",  // New field
    customData: {}  // New field
  };

  // Count techniques and calculate scores
  const techniqueMap = new Map();
  
  actions.forEach(action => {
    if (action.mitreAttackTechnique) {
      const techniqueId = action.mitreAttackTechnique.id;
      
      if (!techniqueMap.has(techniqueId)) {
        techniqueMap.set(techniqueId, {
          techniqueID: techniqueId,
          tacticID: null,  // Will be derived from tactic name
          score: 0,
          color: "",
          comment: "",
          enabled: true,
          metadata: [],
          links: [],
          showSubtechniques: false,
          successful: 0,
          detected: 0,
          blocked: 0,
          failed: 0,
          partial: 0,
          unknown: 0,
          total: 0,
          tactic: action.mitreAttackTechnique.tactic
        });
      }
      
      const technique = techniqueMap.get(techniqueId);
      technique.total++;
      
      // Count results
      switch (action.result) {
        case 'successful':
          technique.successful++;
          break;
        case 'detected':
          technique.detected++;
          break;
        case 'blocked':
          technique.blocked++;
          break;
        case 'failed':
          technique.failed++;
          break;
        case 'partial':
          technique.partial++;
          break;
        case 'unknown':
          technique.unknown++;
          break;
      }
    }
  });

  // Convert to techniques array with scores and colors
  techniqueMap.forEach((technique, techniqueId) => {
    // Calculate score (0-100) based on usage and success
    const successRate = technique.total > 0 ? technique.successful / technique.total : 0;
    const detectionRate = technique.total > 0 ? technique.detected / technique.total : 0;
    const blockRate = technique.total > 0 ? technique.blocked / technique.total : 0;
    
    // Usage weight (0-50 points based on frequency)
    const usageScore = Math.min(technique.total * 5, 30);
    
    // Outcome weight (0-70 points based on results)
    let outcomeScore = 0;
    if (blockRate > 0.5) {
      outcomeScore = 10; // Mostly blocked - low score
    } else if (detectionRate > 0.5) {
      outcomeScore = 40; // Mostly detected - medium score  
    } else if (successRate > 0.5) {
      outcomeScore = 70; // Mostly successful - high score
    } else {
      outcomeScore = 30; // Mixed results
    }
    
    technique.score = Math.round(usageScore + outcomeScore);

    // Set color based on primary result
    if (blockRate >= successRate && blockRate >= detectionRate) {
      technique.color = "#ff6666"; // Red - mostly blocked/failed
    } else if (detectionRate >= successRate) {
      technique.color = "#ffe766"; // Yellow - mostly detected
    } else if (successRate > 0) {
      technique.color = "#8ec843"; // Green - mostly successful
    } else {
      technique.color = "#cccccc"; // Gray - no clear result
    }

    // Enhanced comment with detailed statistics
    technique.comment = `Red Team Activity Summary:\n` +
                       `═══════════════════════════\n` +
                       `Total Attempts: ${technique.total}\n` +
                       `Successful: ${technique.successful} (${(successRate * 100).toFixed(1)}%)\n` +
                       `Detected: ${technique.detected} (${(detectionRate * 100).toFixed(1)}%)\n` +
                       `Blocked: ${technique.blocked} (${(blockRate * 100).toFixed(1)}%)\n` +
                       `Failed: ${technique.failed}\n` +
                       `Partial: ${technique.partial}\n` +
                       `Unknown: ${technique.unknown}\n\n` +
                       `Overall Effectiveness: ${technique.score}/100`;

    // Add metadata for the technique
    technique.metadata = [
      {
        name: "Total Attempts",
        value: technique.total.toString()
      },
      {
        name: "Success Rate", 
        value: `${(successRate * 100).toFixed(1)}%`
      },
      {
        name: "Detection Rate",
        value: `${(detectionRate * 100).toFixed(1)}%`
      },
      {
        name: "Primary Tactic",
        value: technique.tactic
      }
    ];

    layer.techniques.push({
      techniqueID: technique.techniqueID,
      tacticID: technique.tacticID,
      score: technique.score,
      color: technique.color,
      comment: technique.comment,
      enabled: technique.enabled,
      metadata: technique.metadata,
      links: technique.links,
      showSubtechniques: technique.showSubtechniques
    });
  });

  return layer;
};

// Export MITRE Navigator Layer
const exportMitreNavigator = async (projectId = null, actions = null, projectName = null) => {
  try {
    let layerData;

    // First try to get from backend
    if (!actions) {
      const url = projectId 
        ? `${API_URL}mitre-navigator/${projectId}`
        : `${API_URL}mitre-navigator`;
      
      try {
        const response = await axios.get(url);
        layerData = response.data;
      } catch (backendError) {
        console.warn('Backend MITRE Navigator export failed, generating client-side:', backendError);
        
        // If backend fails and we have no actions, throw error
        if (!actions) {
          throw new Error('Unable to generate MITRE Navigator layer: No actions provided and backend export failed');
        }
      }
    }

    // If no backend data, generate client-side
    if (!layerData && actions) {
      layerData = generateMitreNavigatorLayer(actions, projectName || 'Red Team Operations');
    }

    if (!layerData) {
      throw new Error('No data available to generate MITRE Navigator layer');
    }
    
    // Create download link for JSON
    const jsonData = JSON.stringify(layerData, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const sanitizedName = projectName ? projectName.replace(/[^a-zA-Z0-9]/g, '-') : 'red-team-operations';
    link.download = `mitre-navigator-${sanitizedName}-${timestamp}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    return true;
  } catch (error) {
    console.error('Error exporting MITRE Navigator layer:', error);
    throw error;
  }
};

// Export Analytics Dashboard Data
const exportAnalytics = async (projectId = null) => {
  const url = projectId 
    ? `${API_URL}analytics/${projectId}`
    : `${API_URL}analytics`;
    
  try {
    const response = await axios.get(url);
    
    // Create download link for JSON
    const jsonData = JSON.stringify(response.data, null, 2);
    const blob = new Blob([jsonData], { type: 'application/json' });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `analytics-dashboard-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
    
    return true;
  } catch (error) {
    console.error('Error exporting analytics data:', error);
    throw error;
  }
};

// Export All (combined export)
const exportAll = async (projectId = null) => {
  const results = {
    executiveSummary: false,
    excel: false,
    mitreNavigator: false,
    analytics: false
  };
  
  try {
    results.executiveSummary = await exportExecutiveSummary(projectId);
  } catch (error) {
    console.error('Failed to export executive summary:', error);
  }
  
  try {
    results.excel = await exportExcel(projectId);
  } catch (error) {
    console.error('Failed to export Excel data:', error);
  }
  
  try {
    results.mitreNavigator = await exportMitreNavigator(projectId);
  } catch (error) {
    console.error('Failed to export MITRE Navigator layer:', error);
  }
  
  try {
    results.analytics = await exportAnalytics(projectId);
  } catch (error) {
    console.error('Failed to export analytics data:', error);
  }
  
  return results;
};

const exportService = {
  exportExecutiveSummary,
  exportExcel,
  exportMitreNavigator,
  exportAnalytics,
  exportAll,
  generateMitreNavigatorLayer
};

export default exportService;